import { useState } from "react";
import type { Idea, Message, Project, Repo } from "../lib/types";
import { fileIssue } from "../lib/api";
import { toast } from "./Toast";

interface Props {
  idea: Idea;
  messages: Message[];
  projects: Project[];
  repos: Repo[];
  onClose: () => void;
  onFiled: (issueUrl: string) => void;
}

function parseRepoSlug(input: string): string | null {
  input = input.trim();
  if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(input)) return input;
  const sshMatch = input.match(/^git@[^:]+:([^/]+\/[^/]+?)(?:\.git)?$/);
  if (sshMatch) return sshMatch[1];
  try {
    const url = new URL(input);
    const parts = url.pathname.replace(/^\//, "").replace(/\.git$/, "").split("/");
    if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
  } catch { /* not a URL */ }
  return null;
}

function buildInitialBody(idea: Idea, messages: Message[]): string {
  const lastAssistant = [...messages].filter((m) => m.role === "assistant").pop();
  if (lastAssistant) {
    return lastAssistant.content;
  }
  return `## Problem\n\n${idea.brainDump}\n\n## Who is affected\n\n## Proposed solution\n\n## Acceptance Criteria\n`;
}

export function IssueModal({ idea, messages, projects, repos, onClose, onFiled }: Props) {
  const project = projects.find((p) => p.id === idea.projectId);
  const projectRepos = project
    ? repos.filter((r) => project.repos.includes(r.slug))
    : [];
  const otherRepos = repos.filter(
    (r) => !projectRepos.some((pr) => pr.slug === r.slug),
  );
  const orderedRepos = [...projectRepos, ...otherRepos];

  const initialRepo = idea.preferredRepo ?? orderedRepos[0]?.slug ?? "";
  const [repo, setRepo] = useState(initialRepo);
  const [customRepo, setCustomRepo] = useState("");
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(buildInitialBody(idea, messages));
  const [labels, setLabels] = useState("");
  const [filing, setFiling] = useState(false);

  const effectiveRepo = customRepo.trim() ? parseRepoSlug(customRepo) ?? customRepo.trim() : repo;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveRepo || !title.trim()) return;
    setFiling(true);
    try {
      const result = await fileIssue({
        repo: effectiveRepo,
        title: title.trim(),
        body,
        labels: labels.split(",").map((l) => l.trim()).filter(Boolean),
        ideaId: idea.id,
      });
      toast({ kind: "success", message: `Filed issue #${result.number}` });
      onFiled(result.url);
      onClose();
    } catch (err: unknown) {
      toast({ kind: "error", message: err instanceof Error ? err.message : "Failed to file issue" });
    } finally {
      setFiling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl shadow-2xl p-5 pb-8 max-w-[480px] w-full mx-auto max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
        <h2 className="font-syne text-lg font-bold mb-4">File GitHub Issue</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-gray-500 font-mono mb-1 block">Repository</label>
            {orderedRepos.length > 0 ? (
              <select
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800 bg-white"
              >
                {orderedRepos.map((r) => (
                  <option key={r.slug} value={r.slug}>
                    {r.slug}{projectRepos.some((pr) => pr.slug === r.slug) ? " ★" : ""}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              type="text"
              value={customRepo}
              onChange={(e) => setCustomRepo(e.target.value)}
              placeholder={orderedRepos.length ? "Or enter owner/repo…" : "owner/repo"}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800 mt-2"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-mono mb-1 block">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
              required
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-mono mb-1 block">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800 resize-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-mono mb-1 block">Labels (comma-separated)</label>
            <input
              type="text"
              value={labels}
              onChange={(e) => setLabels(e.target.value)}
              placeholder="bug, enhancement"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-gray-800"
            />
          </div>
          <button
            type="submit"
            disabled={filing || !effectiveRepo || !title.trim()}
            className="w-full bg-gray-900 text-white rounded-lg py-2.5 text-sm font-mono font-bold hover:bg-gray-700 transition-colors disabled:opacity-50 mt-1"
          >
            {filing ? "Filing…" : "File Issue"}
          </button>
        </form>
      </div>
    </div>
  );
}
